import {
  Delete,
  InsertDriveFile,
  Visibility,
} from '@mui/icons-material'
import {
  ColumnDef,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table'
import React, { type RefObject, useMemo } from 'react'
import { useMediaQuery } from 'react-responsive'
import type { ESignObjectItem, ESignRecipient, ESignStatus } from '../../lib/api/types/esign.interfaces'
import FileFormatIcon from '../../components/common/FileFormatIcon'
import { SigningStatusIcon } from '../../components/common/SigningStatusIcon'
import { IconButton, Tooltip } from '../../components/ui'
import cn from '../../lib/utils/cn'
import { useUserDateFormat } from '../useUserDateFormat'
import { ReactComponent as SigningSymbol } from '../../assets/icons/interactions/signing-symbol.svg'


export interface ESignTableEntry {
  id: string
  name: string
  status: ESignStatus
  fileId: string
  createdAt?: string
  updatedAt?: string
  recipients: ESignRecipient[]
  objectType?: number
  options: ESignObjectItem
}

interface UseCreateESignDataTableProps {
  data?: ESignObjectItem[] | null
  containerRef?: RefObject<HTMLDivElement>
  onEditItem: (item: ESignObjectItem) => void
  onRenameItem: (item: ESignObjectItem) => void
  onDeleteItem: (item: ESignObjectItem) => void
  onPreviewItem?: (item: ESignObjectItem) => void
  isMobile?: boolean
}

export const ESignObjectIcon = () => (
  <span className='relative w-7 h-7 flex items-center justify-center'>
    <InsertDriveFile className='fill-background-light-hover-dark dark:fill-background-light-hover w-7 h-7' />
    <SigningSymbol className='absolute top-2.5 right-1.5 w-3.5 h-3.5 dark:text-black text-white' />
  </span>
);


export const useCreateESignDataTable = ({
  data = [],
  containerRef,
  onEditItem,
  onRenameItem,
  onDeleteItem,
  onPreviewItem,
  isMobile = false,
}: UseCreateESignDataTableProps) => {
  const { formatDate, formatDateTime } = useUserDateFormat()
  const isCompactMobile = useMediaQuery({ maxWidth: 370 })

  const tableData = useMemo(() => {
    const safeData = Array.isArray(data) ? data : []
    return safeData.map((item) => ({
      id: item.id,
      name: item.title || "Unnamed Item",
      status: item.status,
      fileId: item.fileId,
      createdAt: item.createdAt,
      updatedAt: item.updatedAt,
      recipients: item?.recipients || [],
      objectType: 0,
      options: item,
    }))
  }, [data])

  const columns = useMemo<ColumnDef<ESignTableEntry>[]>(() => {
    const titleColumnSize = isCompactMobile ? 160 : isMobile ? 180 : 260
    const titleColumnMinSize = isCompactMobile ? 120 : isMobile ? 140 : 180
    const statusColumnSize = isCompactMobile ? 52 : isMobile ? 110 : 140
    const statusColumnMinSize = isCompactMobile ? 52 : isMobile ? 90 : 120
    const optionsColumnSize = isCompactMobile ? 48 : isMobile ? 56 : 90
    const optionsColumnMinSize = isCompactMobile ? 48 : isMobile ? 56 : 80

    const renderHeader = (title: string, className?: string) => (
      <div
        className={cn(
          'w-full flex justify-start items-center h-5 pl-4 box-border dark:text-text-table-dark',
          className,
        )}
      >
        <span className="truncate text-left">{title}</span>
      </div>
    )

    const baseColumns: ColumnDef<ESignTableEntry>[] = [
      {
        accessorKey: 'name',
        header: () => renderHeader('Title'),
        size: titleColumnSize,
        minSize: titleColumnMinSize,
        cell: ({ row }) => {
          const title = row.original.name
          const status = row.original.status
          return (
            <div className="pl-3 md:pl-4 flex items-center justify-start gap-2 min-w-0">
              {status === 'SIGNED' ?
                <FileFormatIcon extension='pdf' /> :
                <ESignObjectIcon />
              }
              <Tooltip label={title} className={cn('opacity-0 md:opacity-100')}>
                <p className="truncate dark:text-text-table-dark">{title}</p>
              </Tooltip>
            </div>
          )
        },
      },
      {
        accessorKey: 'status',
        header: () => renderHeader(isCompactMobile ? '' : 'Status'),
        size: statusColumnSize,
        minSize: statusColumnMinSize,
        cell: (info) => {
          const status = info.getValue() as ESignStatus | undefined
          if (!status) return <span>-</span>
          const normalizedStatus = String(status).replace(/_/g, ' ')

          return (
            <div
              className={cn(
                'flex items-center min-w-0',
                isCompactMobile ? 'justify-center' : 'gap-1',
              )}
            >
              <SigningStatusIcon status={status} />
              {!isCompactMobile && <span className="truncate">{normalizedStatus}</span>}
            </div>
          )
        },
      },
      // {
      //   accessorKey: 'fileId',
      //   header: () => renderHeader('File ID'),
      //   size: 220,
      //   minSize: 180,
      //   cell: (info) => (
      //     <Tooltip label={String(info.getValue() ?? '')}>
      //       <p className="truncate">{String(info.getValue() ?? '-')}</p>
      //     </Tooltip>
      //   ),
      // },
      {
        accessorKey: 'recipients',
        header: () => renderHeader('Recipients'),
        size: 100,
        minSize: 90,
        cell: (info) => {
          return <span>{info?.row?.original?.recipients?.length}</span>
        }
      },
      {
        accessorKey: 'updatedAt',
        header: () => renderHeader('Updated At'),
        size: 130,
        minSize: 120,
        cell: (info) => {
          const value = info.getValue()
          if (!value) return '-'
          const stringValue = String(value)
          return (
            <Tooltip label={formatDateTime(stringValue)} classNameChildrenWrapper="w-fit">
              <span>{formatDate(stringValue)}</span>
            </Tooltip>
          )
        },
      },
      {
        accessorKey: 'options',
        enableResizing: false,
        enableSorting: false,
        header: () => renderHeader('', 'justify-end pr-3 pl-0'),
        size: optionsColumnSize,
        minSize: optionsColumnMinSize,
        maxSize: optionsColumnSize,
        cell: ({ row }) => {
          const item = row.original.options

          return (
            <div className={cn(
              'flex items-center justify-end gap-2',
              isCompactMobile ? 'pr-1' : isMobile ? 'pr-2' : 'pr-3',
            )}>
              {/* <IconButton
                className="hidden md:flex w-8 h-8 rounded-lg"
                variant="secondary"
                icon={<Edit className="w-4 h-4 dark:fill-text-main-dark" />}
                tooltip="Edit"
                onClick={() => onEditItem(item)}
              /> */}
              {item?.status === 'DRAFT' && (
                <IconButton
                  className={cn(
                    'w-8 h-8 rounded-lg',
                    isMobile ? 'flex' : 'hidden md:flex',
                  )}
                  variant="secondary"
                  icon={<Delete className="w-4 h-4 dark:fill-text-main-dark" />}
                  tooltip="Delete"
                  onClick={() => onDeleteItem(item)}
                />
              )}
              {item?.status === 'SIGNED' && !!onPreviewItem && (
                <IconButton
                  className={cn(
                    'w-8 h-8 rounded-lg',
                    isMobile ? 'flex' : 'hidden md:flex',
                  )}
                  variant="secondary"
                  icon={<Visibility className="w-4 h-4 dark:fill-text-main-dark" />}
                  tooltip="Preview"
                  onClick={() => onPreviewItem(item)}
                />
              )}

            </div>
          )
        },
      },
    ]

    if (isMobile) {
      return baseColumns.filter((columnDef) => {
        const accessorKey = 'accessorKey' in columnDef ? columnDef.accessorKey : undefined
        return accessorKey && ['name', 'status', 'options'].includes(String(accessorKey))
      })
    }

    return baseColumns
  }, [containerRef, formatDate, formatDateTime, isCompactMobile, isMobile, onDeleteItem, onEditItem, onPreviewItem, onRenameItem])

  const table = useReactTable({
    data: tableData,
    columns,
    columnResizeMode: 'onChange',
    getCoreRowModel: getCoreRowModel(),
  })

  return { table }
}
